import type { BankFootageQuery } from 'fym-shared/src/music24/musicVideoApi';
import type { BankMood, BankSetting } from 'fym-shared/src/music24/reelBankTaxonomy';

/** The filter chips of the bank (design b1). Each maps to one field of the bank taxonomy. */
export const BANK_FILTER_CHIPS = ['noPeople', 'night', 'nature', 'calm', 'euphoric'] as const;
export type BankFilterChip = (typeof BANK_FILTER_CHIPS)[number];

const CHIP_SETTING: Partial<Record<BankFilterChip, BankSetting>> = { night: 'city-night', nature: 'nature' };
const CHIP_MOOD: Partial<Record<BankFilterChip, BankMood>> = { calm: 'chill', euphoric: 'euphoric' };

/** One mood and one setting at a time: a chip of the same field replaces the other. Pure. */
export function toggleBankChip(picked: readonly BankFilterChip[], chip: BankFilterChip): BankFilterChip[] {
  if (picked.includes(chip)) {
    return picked.filter((item) => item !== chip);
  }
  const sameField = (item: BankFilterChip) =>
    (CHIP_SETTING[chip] !== undefined && CHIP_SETTING[item] !== undefined) ||
    (CHIP_MOOD[chip] !== undefined && CHIP_MOOD[item] !== undefined);
  return [...picked.filter((item) => !sameField(item)), chip];
}

/** The query of `GET /footage/bank` for a search text and the picked chips. Pure. */
export function bankQueryOf(text: string, picked: readonly BankFilterChip[]): BankFootageQuery {
  const query: BankFootageQuery = {};
  const q = text.trim();
  if (q !== '') {
    query.q = q;
  }
  for (const chip of picked) {
    const setting = CHIP_SETTING[chip];
    const mood = CHIP_MOOD[chip];
    if (setting !== undefined) {
      query.setting = setting;
    }
    if (mood !== undefined) {
      query.mood = mood;
    }
    if (chip === 'noPeople') {
      query.people = 'none';
    }
  }
  return query;
}

/** True when the search or a chip asks for a list of the bank (else the panel shows the suggestions). */
export function isBankSearch(query: BankFootageQuery): boolean {
  return query.q !== undefined || query.mood !== undefined || query.setting !== undefined || query.people !== undefined;
}
